import { createBrowserRouter, Outlet } from 'react-router-dom';
import { App } from './App';
import { AuthGuard } from '@/features/auth/components/AuthGuard';
import { RouteErrorElement } from '@/shared/components/RouteErrorElement';
import { PublicLanguageSelector } from '@/shared/components/LanguageSelector';
import { lazyWithRefresh } from '@/shared/lib/lazyRetry';

export const router = createBrowserRouter([
  {
    // Rutas de auth (redirigen a / si ya hay sesión)
    element: (
      <AuthGuard requireAuth={false}>
        <PublicLanguageSelector />
        <Outlet />
      </AuthGuard>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      {
        path: 'login',
        lazy: () => lazyWithRefresh(async () => {
          const { LoginPage } = await import('@/features/auth/pages/LoginPage');
          return { Component: LoginPage };
        }),
      },
      {
        path: 'register',
        lazy: () => lazyWithRefresh(async () => {
          const { RegisterPage } = await import('@/features/auth/pages/RegisterPage');
          return { Component: RegisterPage };
        }),
      },
      {
        path: 'register/check-email',
        lazy: () => lazyWithRefresh(async () => {
          const { CheckEmailPage } = await import('@/features/auth/pages/CheckEmailPage');
          return { Component: CheckEmailPage };
        }),
      },
      {
        path: 'forgot-password',
        lazy: () => lazyWithRefresh(async () => {
          const { ForgotPasswordPage } = await import('@/features/auth/pages/ForgotPasswordPage');
          return { Component: ForgotPasswordPage };
        }),
      },
    ],
  },
  {
    // Reset password: Supabase Auth deja al usuario aquí con una sesión temporal
    // tras el clic en el email. NO va bajo AuthGuard porque la sesión aún se
    // procesa por supabase-js al detectar el fragment. La página lo maneja.
    path: 'auth/reset-password',
    errorElement: <RouteErrorElement />,
    element: (
      <>
        <PublicLanguageSelector />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        lazy: () => lazyWithRefresh(async () => {
          const { ResetPasswordPage } = await import('@/features/auth/pages/ResetPasswordPage');
          return { Component: ResetPasswordPage };
        }),
      },
    ],
  },
  {
    // Legales y baja de cuenta: **públicas a propósito**, fuera del AuthGuard.
    //
    // Google Play exige una URL de política de privacidad y otra para pedir la
    // baja, y **las visita su revisor sin cuenta**: una que redirige al login se
    // da por no válida y tumba la revisión. Además la app nativa enlaza a
    // `/privacy` desde Perfil, así que estando protegida no podía leerla quien
    // acaba de instalar — justo cuando hace falta. Ver incidencia #34.
    //
    // No van bajo `AuthGuard requireAuth={false}` como las de auth: ése **echa a
    // quien sí tiene sesión** hacia `/`, y a estas páginas se llega desde Perfil
    // estando dentro. Tienen que servir a los dos.
    //
    // Contrapartida asumida: quien entre con sesión las ve sin la barra de
    // navegación de la aplicación. Se acepta porque el «atrás» le devuelve a
    // donde estaba y porque envolverlas en el armazón obligaría a montarlo sin
    // sesión para el caso anónimo, que es el que importa aquí.
    errorElement: <RouteErrorElement />,
    element: (
      <>
        <PublicLanguageSelector />
        <Outlet />
      </>
    ),
    children: [
      {
        path: 'privacy',
        lazy: () => lazyWithRefresh(async () => {
          const { PrivacyPage } = await import('@/features/legal/pages/PrivacyPage');
          return { Component: PrivacyPage };
        }),
      },
      {
        path: 'terms',
        lazy: () => lazyWithRefresh(async () => {
          const { TermsPage } = await import('@/features/legal/pages/TermsPage');
          return { Component: TermsPage };
        }),
      },
      {
        // En castellano y no `delete-account`: es la URL que se pega en el
        // formulario de Play y la lee gente, no código.
        path: 'eliminar-cuenta',
        lazy: () => lazyWithRefresh(async () => {
          const { DeleteAccountInfoPage } = await import(
            '@/features/legal/pages/DeleteAccountInfoPage'
          );
          return { Component: DeleteAccountInfoPage };
        }),
      },
    ],
  },
  {
    // Callback OAuth
    path: 'auth/callback',
    errorElement: <RouteErrorElement />,
    lazy: () => lazyWithRefresh(async () => {
      const { AuthCallbackPage } = await import('@/features/auth/pages/AuthCallbackPage');
      return { Component: AuthCallbackPage };
    }),
  },
  {
    // Landing de invitación (sin AuthGuard: gestiona ambos casos)
    path: 'invite/:token',
    errorElement: <RouteErrorElement />,
    lazy: () => lazyWithRefresh(async () => {
      const { AcceptInvitationPage } = await import(
        '@/features/invitations/pages/AcceptInvitationPage'
      );
      return { Component: AcceptInvitationPage };
    }),
  },
  {
    // Shell protegido
    path: '/',
    element: (
      <AuthGuard requireAuth>
        <App />
      </AuthGuard>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      {
        index: true,
        lazy: () => lazyWithRefresh(async () => {
          const { HomePage } = await import('@/features/home/pages/HomePage');
          return { Component: HomePage };
        }),
      },
      {
        path: 'groups',
        lazy: () => lazyWithRefresh(async () => {
          const { GroupsPage } = await import('@/features/groups/pages/GroupsPage');
          return { Component: GroupsPage };
        }),
      },
      {
        path: 'activity',
        lazy: () => lazyWithRefresh(async () => {
          const { ActivityPage } = await import('@/features/activity/pages/ActivityPage');
          return { Component: ActivityPage };
        }),
      },
      {
        path: 'groups/new',
        lazy: () => lazyWithRefresh(async () => {
          const { CreateGroupPage } = await import('@/features/groups/pages/CreateGroupPage');
          return { Component: CreateGroupPage };
        }),
      },
      {
        path: 'groups/:groupId',
        lazy: () => lazyWithRefresh(async () => {
          const { GroupPage } = await import('@/features/groups/pages/GroupPage');
          return { Component: GroupPage };
        }),
      },
      {
        path: 'groups/:groupId/edit',
        lazy: () => lazyWithRefresh(async () => {
          const { EditGroupPage } = await import('@/features/groups/pages/EditGroupPage');
          return { Component: EditGroupPage };
        }),
      },
      {
        path: 'groups/:groupId/expenses/new',
        lazy: () => lazyWithRefresh(async () => {
          const { CreateExpensePage } = await import(
            '@/features/expenses/pages/CreateExpensePage'
          );
          return { Component: CreateExpensePage };
        }),
      },
      {
        path: 'groups/:groupId/expenses/:expenseId/edit',
        lazy: () => lazyWithRefresh(async () => {
          const { CreateExpensePage } = await import(
            '@/features/expenses/pages/CreateExpensePage'
          );
          return { Component: CreateExpensePage };
        }),
      },
      {
        path: 'groups/:groupId/expenses/:expenseId',
        lazy: () => lazyWithRefresh(async () => {
          const { ExpensePage } = await import('@/features/expenses/pages/ExpensePage');
          return { Component: ExpensePage };
        }),
      },
      {
        path: 'groups/:groupId/analytics',
        lazy: () => lazyWithRefresh(async () => {
          const { AnalyticsPage } = await import('@/features/analytics/pages/AnalyticsPage');
          return { Component: AnalyticsPage };
        }),
      },
      {
        path: 'groups/:groupId/debts',
        lazy: () => lazyWithRefresh(async () => {
          const { DebtsPage } = await import('@/features/debts/pages/DebtsPage');
          return { Component: DebtsPage };
        }),
      },
      {
        path: 'groups/:groupId/expenses',
        lazy: () => lazyWithRefresh(async () => {
          const { AllExpensesPage } = await import('@/features/expenses/pages/AllExpensesPage');
          return { Component: AllExpensesPage };
        }),
      },
      {
        path: 'groups/:groupId/recurring',
        lazy: () => lazyWithRefresh(async () => {
          const { RecurringExpensesPage } = await import('@/features/recurring/pages/RecurringExpensesPage');
          return { Component: RecurringExpensesPage };
        }),
      },
      {
        path: 'profile',
        lazy: () => lazyWithRefresh(async () => {
          const { ProfilePage } = await import('@/features/profile/pages/ProfilePage');
          return { Component: ProfilePage };
        }),
      },
      {
        path: 'notifications',
        lazy: () => lazyWithRefresh(async () => {
          const { NotificationsPage } = await import('@/features/notifications/pages/NotificationsPage');
          return { Component: NotificationsPage };
        }),
      },
      {
        path: 'notifications/preferences',
        lazy: () => lazyWithRefresh(async () => {
          const { NotificationPreferencesPage } = await import('@/features/notifications/pages/NotificationPreferencesPage');
          return { Component: NotificationPreferencesPage };
        }),
      },
      {
        path: 'stats',
        lazy: () => lazyWithRefresh(async () => {
          const { StatsPage } = await import('@/features/analytics/pages/StatsPage');
          return { Component: StatsPage };
        }),
      },
      {
        path: 'admin',
        lazy: () => lazyWithRefresh(async () => {
          const { AdminPage } = await import('@/features/admin/pages/AdminPage');
          return { Component: AdminPage };
        }),
      },
      {
        path: 'admin/moderation',
        lazy: () => lazyWithRefresh(async () => {
          const { ModerationPage } = await import('@/features/moderation/pages/ModerationPage');
          return { Component: ModerationPage };
        }),
      },
      {
        path: 'admin/taxonomy',
        lazy: () => lazyWithRefresh(async () => {
          const { TaxonomyPage } = await import('@/features/moderation/pages/TaxonomyPage');
          return { Component: TaxonomyPage };
        }),
      },
      {
        path: 'admin/backups',
        lazy: () => lazyWithRefresh(async () => {
          const { BackupsPage } = await import('@/features/backups/pages/BackupsPage');
          return { Component: BackupsPage };
        }),
      },
      {
        path: 'import',
        lazy: () => lazyWithRefresh(async () => {
          const { ImportLandingPage } = await import('@/features/import/pages/ImportLandingPage');
          return { Component: ImportLandingPage };
        }),
      },
      {
        path: 'groups/:id/import',
        lazy: () => lazyWithRefresh(async () => {
          const { ImportPage } = await import('@/features/import/pages/ImportPage');
          return { Component: ImportPage };
        }),
      },
      {
        path: 'admin/releases',
        lazy: () => lazyWithRefresh(async () => {
          const { AdminReleasesPage } = await import('@/features/releases/pages/AdminReleasesPage');
          return { Component: AdminReleasesPage };
        }),
      },
      {
        path: 'admin/system-messages',
        lazy: () => lazyWithRefresh(async () => {
          const { AdminSystemMessagesPage } = await import('@/features/releases/pages/AdminSystemMessagesPage');
          return { Component: AdminSystemMessagesPage };
        }),
      },
      // Sprint 3+:
      // { path: 'activity', ... },
      // { path: 'stats', ... },
      // { path: 'admin/moderation', ... },
    ],
  },
], {
  // Respeta el --base de Vite. En prod es "/", en staging es "/beta/".
  // React Router quiere el basename SIN barra final.
  basename: import.meta.env.BASE_URL.replace(/\/$/, ''),
});
